import type { ReactElement } from 'react';
import { SEAT_CLASSES, type CabinMetrics } from '../../../domain/models';
import { InfoPopover } from '../common/InfoPopover';
import { CLASS_LABELS, formatPercent, formatUnits } from './cabinFormat';

export interface FloorBudgetBarProps {
  readonly metrics: CabinMetrics;
}

/**
 * Floor unit budget as a stacked bar: one segment per seat class. When the
 * layout overflows, the bar rescales so the over-budget part stays visible and
 * a marker shows where the budget ends.
 */
export function FloorBudgetBar({ metrics }: FloorBudgetBarProps): ReactElement {
  const { floorUnitsBudget, floorUnitsUsed, floorUnitsByClass } = metrics;
  const overBudget = floorUnitsUsed > floorUnitsBudget;
  const scale = Math.max(floorUnitsBudget, floorUnitsUsed, 1);
  const ratio = floorUnitsBudget > 0 ? floorUnitsUsed / floorUnitsBudget : 0;
  const budgetMarkerPct = (floorUnitsBudget / scale) * 100;
  const difference = Math.abs(floorUnitsBudget - floorUnitsUsed);

  const status = overBudget
    ? `${formatUnits(difference)} units over budget`
    : difference < 0.05
      ? 'Budget fully allocated'
      : `${formatUnits(difference)} units free`;

  return (
    <section className="ce-card" aria-labelledby="ce-floor-title">
      <div className="ce-floor__header">
        <h3 id="ce-floor-title" className="ce-card__title">
          Cabin floor budget
          <InfoPopover topic="floorSpaceUnits" />
        </h3>
        <p
          className={`ce-floor__figures${overBudget ? ' ce-floor__figures--over' : ''}`}
          aria-live="polite"
        >
          <strong>{formatUnits(floorUnitsUsed)}</strong> / {formatUnits(floorUnitsBudget)} units
          <span className="ce-floor__percent"> · {formatPercent(ratio)}</span>
        </p>
      </div>

      <div
        className={`ce-floor__track${overBudget ? ' ce-floor__track--over' : ''}`}
        role="progressbar"
        aria-label="Floor units allocated"
        aria-valuemin={0}
        aria-valuemax={floorUnitsBudget}
        aria-valuenow={Math.min(floorUnitsUsed, floorUnitsBudget)}
        aria-valuetext={`${formatUnits(floorUnitsUsed)} of ${formatUnits(floorUnitsBudget)} units, ${status}`}
      >
        {SEAT_CLASSES.map((seatClass) => {
          const units = floorUnitsByClass[seatClass];
          if (!(units > 0)) {
            return null;
          }
          return (
            <span
              key={seatClass}
              className="ce-floor__segment"
              data-seat-class={seatClass}
              style={{ width: `${(units / scale) * 100}%` }}
              title={`${CLASS_LABELS[seatClass]}: ${formatUnits(units)} units`}
            />
          );
        })}
        {overBudget ? (
          <span
            className="ce-floor__marker"
            style={{ left: `${budgetMarkerPct}%` }}
            aria-hidden="true"
          />
        ) : null}
      </div>

      <p className={`ce-floor__status${overBudget ? ' ce-floor__status--over' : ''}`}>{status}</p>

      <ul className="ce-legend">
        {SEAT_CLASSES.map((seatClass) => (
          <li key={seatClass} className="ce-legend__item">
            <span className="ce-legend__swatch" data-seat-class={seatClass} aria-hidden="true" />
            {CLASS_LABELS[seatClass]}
            <span className="ce-legend__value">{formatUnits(floorUnitsByClass[seatClass])}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
